// Client360 (TASK-496) — ported from SixEase widgets/Client360.tsx, re-grounded on Kielis
// services and re-tokenised. DNA (values/red-lines/context/life-events) + a portfolio health
// strip (fit + total value + max drift). "Deep Dive Portfolio" appends the CockpitPortfolioView
// widget to the canvas via canvasStore.addSpecs instead of navigating away. client_name +
// total_value come from getPortfolioOverview (Kielis DnaResponse lacks client_name).
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, Target, User, Activity } from 'lucide-react';
import { type DnaResponse, type DnaItem } from '../../../services/knowledgeGraphService';
import { portfolioApiService } from '../../../services/api/portfolioApiService';
import { useFit } from '../../../hooks/useFit';
import { useDrift } from '../../../hooks/useDrift';
import { useClientDna } from '../../../hooks/useClientDna';
import { useCanvasStore } from '../../../stores/canvasStore';
import { WidgetContainer } from '../WidgetContainer';

const RISK_BY_MANDATE: Record<string, string> = {
  DEFENSIVE: 'Low',
  BALANCED: 'Medium',
  GROWTH: 'High',
};

function texts(items: DnaItem[] | null | undefined, n = 8): string[] {
  // Deduped: the chips below are keyed on the value itself, so two DNA entries with identical
  // text collided into a React duplicate-key warning. Showing the same conviction twice was
  // never useful either — one chip per distinct statement is the intent.
  const seen = new Set<string>();
  return (items ?? [])
    .map((i) => i.text)
    .filter((t): t is string => Boolean(t) && !seen.has(t) && (seen.add(t), true))
    .slice(0, n);
}

function chfCompact(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)}K`;
  return `${Math.round(v)}`;
}

interface Loaded {
  dna: DnaResponse;
  clientName: string;
  totalValue: number | null;
  fitScore: number | null;
  maxDrift: number | null;
}

export function Client360({ clientId }: { clientId: string }) {
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  // DNA is required (gates error); the rest enrich the health strip and degrade to null.
  const dnaQuery = useClientDna(clientId);
  const fitQuery = useFit(clientId);
  const driftQuery = useDrift(clientId);
  const ovQuery = useQuery({
    queryKey: ['portfolio-overview', clientId],
    queryFn: () => portfolioApiService.getPortfolioOverview(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });

  // Atomic reveal: wait for every fetch to settle before showing the body (no half-populated flash).
  const loading =
    dnaQuery.isLoading || ovQuery.isLoading || fitQuery.isLoading || driftQuery.isLoading;

  const d: Loaded | null = dnaQuery.data
    ? (() => {
        const driftVals = Object.values(driftQuery.data?.drift?.drift_by_asset_class ?? {});
        return {
          dna: dnaQuery.data,
          clientName: ovQuery.data?.client_name ?? '',
          totalValue: ovQuery.data?.total_value ?? null,
          fitScore:
            fitQuery.data?.portfolio_fit != null ? Math.round(fitQuery.data.portfolio_fit * 100) : null,
          maxDrift: driftVals.length ? Math.max(...driftVals.map((v) => Math.abs(v.drift))) : null,
        };
      })()
    : null;

  return (
    <WidgetContainer source="Client 360">
      {dnaQuery.isError && (
        <div className="text-[13px]">
          <p className="text-muted-foreground">Could not load client profile.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            {dnaQuery.error instanceof Error ? dnaQuery.error.message : String(dnaQuery.error)}
          </p>
        </div>
      )}
      {!dnaQuery.isError && loading && <div className="h-64 animate-pulse rounded-xl bg-accent" />}
      {!dnaQuery.isError && !loading && d && (
        <Client360Body d={d} onDeepDive={() => addSpecs([{ component: 'CockpitPortfolioView', props: { clientId } }])} />
      )}
    </WidgetContainer>
  );
}

function Client360Body({ d, onDeepDive }: { d: Loaded; onDeepDive: () => void }) {
  const { dna } = d;
  const mandate = dna.mandate ?? '—';
  const risk = RISK_BY_MANDATE[mandate.toUpperCase()] ?? '—';
  const values = texts(dna.values);
  const tilts = texts(dna.tilts);
  const redLines = texts(dna.exclusions);
  const lifeEvents = texts(dna.life_events);
  const context = dna.family_context || dna.business_context || '—';

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-foreground">{d.clientName || 'Client'} — Snapshot</h2>
        <p className="text-sm text-muted-foreground">{mandate} mandate</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* Client Profile */}
        <div className="rounded-xl border border-border bg-muted/40 p-3 lg:col-span-4">
          <h3 className="mb-2 text-[13px] font-semibold text-foreground">Client Profile</h3>
          <div className="space-y-1 text-sm">
            <Row label="Total Value" value={d.totalValue != null ? `CHF ${chfCompact(d.totalValue)}` : '—'} />
            <Row label="Risk Profile" value={risk} />
            <Row label="Mandate" value={mandate} />
            <Row label="Temperament" value={dna.temperament || '—'} last />
          </div>
        </div>

        {/* Client DNA */}
        <div className="rounded-xl border border-border bg-muted/40 p-3 lg:col-span-8">
          <h3 className="mb-2 text-[13px] font-semibold text-foreground">Client DNA</h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <SectionLabel icon={<Target className="h-3 w-3" />}>Core Values &amp; Preferences</SectionLabel>
              <div className="mb-4 flex flex-wrap gap-2">
                {values.map((v) => (
                  <span key={v} className="rounded border border-primary/20 bg-primary/10 px-2 py-1 text-xs text-primary">
                    {v}
                  </span>
                ))}
                {tilts.map((v) => (
                  <span key={v} className="rounded border border-border bg-accent px-2 py-1 text-xs text-muted-foreground">
                    {v}
                  </span>
                ))}
                {values.length === 0 && tilts.length === 0 && <Empty />}
              </div>

              <SectionLabel icon={<ShieldAlert className="h-3 w-3" />} tone="red">
                Red Lines
              </SectionLabel>
              <ul className="space-y-2">
                {redLines.length ? (
                  redLines.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-foreground">
                      <span className="mt-1 text-destructive">•</span> {r}
                    </li>
                  ))
                ) : (
                  <Empty />
                )}
              </ul>
            </div>

            <div>
              <SectionLabel icon={<User className="h-3 w-3" />}>Context</SectionLabel>
              <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{context}</p>

              <SectionLabel icon={<Activity className="h-3 w-3" />}>Life Events</SectionLabel>
              <ul className="space-y-2">
                {lifeEvents.length ? (
                  lifeEvents.map((e) => (
                    <li key={e} className="flex items-start gap-2 text-sm text-foreground">
                      <span className="mt-1 text-primary">•</span> {e}
                    </li>
                  ))
                ) : (
                  <Empty />
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Health strip */}
      <div className="rounded-xl border border-border bg-muted/40 p-3">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-[13px] font-semibold text-foreground">Portfolio Health</h3>
          {d.fitScore != null && (
            <span className="rounded-full bg-emerald-500/20 px-2 text-[10px] font-bold uppercase tracking-wider text-emerald-600">
              Fit Score: {d.fitScore}
            </span>
          )}
        </div>
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex gap-8">
            <div>
              <div className="mb-1 text-xs text-muted-foreground/70">Total Value</div>
              <div className="text-xl font-semibold text-foreground">
                {d.totalValue != null ? `CHF ${chfCompact(d.totalValue)}` : '—'}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs text-muted-foreground/70">Max Drift</div>
              <div className="text-xl font-semibold text-amber-500">{d.maxDrift != null ? `${d.maxDrift.toFixed(1)}pp` : '—'}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onDeepDive}
            className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:w-auto"
          >
            Deep Dive Portfolio
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    // gap-4 + text-right: long narrative values (Temperament) used to render
    // flush against the label with no separation (TASK-600 audit follow-up).
    <div className={`flex justify-between gap-4 py-2 ${last ? '' : 'border-b border-border'}`}>
      <span className="shrink-0 text-muted-foreground/70">{label}</span>
      <span className="min-w-0 text-right text-foreground">{value}</span>
    </div>
  );
}

function SectionLabel({ icon, children, tone }: { icon: React.ReactNode; children: React.ReactNode; tone?: 'red' }) {
  return (
    <h4
      className={`mb-3 mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider first:mt-0 ${
        tone === 'red' ? 'text-destructive' : 'text-muted-foreground'
      }`}
    >
      {icon} {children}
    </h4>
  );
}

function Empty() {
  return <span className="text-xs text-muted-foreground/70">None recorded.</span>;
}
